import type {
  ActivateRequest,
  ActivityEntry,
  AdministratorItem,
  ApiEnvelope,
  AuthSession,
  Certificate,
  CertificateEvidence,
  CertificateRequest,
  CertificateSubjectType,
  CertificateUploadContext,
  CertificateUploadResult,
  CurateProposedDeviceRequest,
  CurateProposedVendorRequest,
  Company,
  CompanyWithCounts,
  CreateCertificateRequestBody,
  CreateCompanyRequest,
  CreateFrameworkRequest,
  CreateReportRequest,
  CreateSiteRequest,
  CreateUserRequest,
  DashboardSummary,
  DeclareCertificateRequest,
  DeclaredCertificate,
  EvidenceDownloadResponse,
  Framework,
  FrameworkDetail,
  ImportCommitRequest,
  ImportResult,
  NeedsAttentionSurface,
  NotificationList,
  DeviceCatalogEntry,
  DeviceDirectoryEntry,
  DeviceDuplicateCheckResponse,
  DeviceInstall,
  DeviceRemoval,
  HealthStatus,
  Invitation,
  InviteRequest,
  LoginRequest,
  ProposeDeviceRequest,
  ProposeVendorRequest,
  RequestsInbox,
  SubmitCertificateForVerificationRequest,
  ResolveOnboardingResult,
  SeedCertificateListing,
  Site,
  SiteComplianceDetail,
  SiteVendorAssignment,
  UpdateFrameworkRequest,
  UpdateSiteRequest,
  UpdateUserProfileRequest,
  UserItem,
  UserSettings,
  UserSettingsPatch,
  Vendor,
  VendorDirectoryEntry,
  VendorOnboardingFlag,
  VendorPortalOverview,
  VerificationQueueItem,
  VerifiedCertificateItem,
} from '@mvcp/contracts';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * The access token is held in a module-scoped variable — in MEMORY ONLY. It is
 * never written to localStorage/sessionStorage (FR-2, AR-15/AD-12). A full page
 * reload loses it; the session is then restored via the httpOnly refresh cookie
 * (`refreshSession`), not from web storage.
 */
let accessToken: string | null = null;

/** Shared in-flight refresh so concurrent 401s don't stampede /auth/refresh. */
let refreshInFlight: Promise<AuthSession> | null = null;

/** Set by AuthContext so a failed reactive refresh can route back to Login. */
let onSessionExpired: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setOnSessionExpired(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

async function unwrap<T>(res: Response): Promise<T> {
  // Tolerate non-JSON/empty bodies (proxy/gateway 502/504 HTML, 204) and
  // malformed envelopes instead of throwing an opaque SyntaxError/TypeError.
  const body = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;
  if (!res.ok || body === null || !body.ok) {
    const message =
      body !== null && !body.ok ? (body.error?.message ?? `HTTP ${res.status}`) : `HTTP ${res.status}`;
    throw new Error(message);
  }
  return body.data;
}

/** POST /api/v1/auth/login — exchanges credentials for an in-memory access token. */
export async function login(credentials: LoginRequest): Promise<AuthSession> {
  const res = await fetch(`${API_BASE}/api/v1/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(credentials),
  });
  const session = await unwrap<AuthSession>(res);
  accessToken = session.accessToken;
  return session;
}

/**
 * POST /api/v1/auth/refresh — silent renew against the httpOnly refresh cookie.
 * Single-flight: concurrent callers share one in-flight request so refresh-token
 * rotation can't invalidate a sibling's cookie (which would force a bogus logout).
 */
export function refreshSession(): Promise<AuthSession> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { accept: 'application/json' },
    });
    const session = await unwrap<AuthSession>(res);
    accessToken = session.accessToken;
    return session;
  })();
  const pending = refreshInFlight;
  // Reset the single-flight latch once the request settles. `.finally` re-raises
  // a rejection onto its own chain, so swallow it here with `.catch`: the failure
  // is still delivered to real callers via their `pending` reference (below) — this
  // bookkeeping branch must not surface as an orphan unhandled rejection.
  void pending
    .finally(() => {
      if (refreshInFlight === pending) refreshInFlight = null;
    })
    .catch(() => {});
  return pending;
}

/** POST /api/v1/auth/logout — revokes the session and clears the in-memory token. */
export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/v1/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { accept: 'application/json' },
    });
  } finally {
    accessToken = null;
  }
}

/**
 * Fetch a protected resource with the in-memory bearer token. On a 401, attempt
 * ONE silent refresh and retry; if the refresh fails, clear the token and
 * rethrow so the caller can route back to Login (AC3).
 */
export async function authFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const withAuth = (): RequestInit => ({
    ...init,
    credentials: 'include',
    headers: {
      ...(init.headers ?? {}),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  let res = await fetch(`${API_BASE}${path}`, withAuth());
  if (res.status === 401) {
    try {
      await refreshSession();
    } catch (err) {
      accessToken = null;
      // Signal AuthContext so the shell routes back to Login (Story 1.2 AC3);
      // clearing the token alone leaves status='authenticated'.
      onSessionExpired?.();
      throw err instanceof Error ? err : new Error('Session expired');
    }
    res = await fetch(`${API_BASE}${path}`, withAuth());
  }
  return res;
}

/** POST /api/v1/invitations — System Administrator only. Returns the invitation (dev: with activationLink). */
export async function inviteUser(input: InviteRequest): Promise<Invitation> {
  return unwrap<Invitation>(
    await authFetch('/api/v1/invitations', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

/**
 * GET /api/v1/admin/administrators — the platform's System Administrators (Story
 * 6.3, FR-30). System-Administrator only (RolesGuard denies any other principal
 * 403). Returns activated admins plus pending admin invitations, each tagged
 * `active`/`pending`. No company/tenant identity is present in any item.
 */
export async function listAdministrators(): Promise<AdministratorItem[]> {
  return unwrap<AdministratorItem[]>(
    await authFetch('/api/v1/admin/administrators', { headers: { accept: 'application/json' } }),
  );
}

/**
 * GET /api/v1/admin/users — the full platform Users roster (Story 6.5, FR-29/FR-30).
 * System-Administrator only (RolesGuard denies any other principal 403). Returns every
 * user across all Companies and the Vendor Directory — active, deactivated, and
 * pending-invite — incl. users provisioned directly in Keycloak (backfilled).
 */
export async function listUsers(): Promise<UserItem[]> {
  return unwrap<UserItem[]>(
    await authFetch('/api/v1/admin/users', { headers: { accept: 'application/json' } }),
  );
}

/**
 * POST /api/v1/admin/users/:id/deactivate — disable a user's access (Story 6.5, AC2).
 * System-Administrator only. Disables the account in Keycloak + revokes sessions, flips
 * the roster status to `deactivated`, and audits the reason — all atomic. Returns 204;
 * a blank reason is a 400, an unknown id a 404, and the last active admin a 409.
 */
export async function deactivateUser(id: string, reason: string): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/admin/users/${id}/deactivate`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ reason }),
    }),
  );
}

/**
 * POST /api/v1/admin/users/:id/reactivate — restore a user's access (Story 6.5, AC2).
 * System-Administrator only. Re-enables sign-in in Keycloak, flips the roster status to
 * `active`, and audits — all atomic. Returns 204; an unknown id is a 404; idempotent
 * when already active.
 */
export async function reactivateUser(id: string): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/admin/users/${id}/reactivate`, {
      method: 'POST',
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * POST /api/v1/admin/invitations/:id/resend — resend a pending invitation (Story 6.5,
 * AC3). System-Administrator only. Mints a fresh single-use magic link (invalidating the
 * prior token), re-sends the invite email, and audits. Returns the invitation (dev: with
 * activationLink); a 404 unknown id; a 409 when not pending/expired.
 */
export async function resendInvitation(id: string): Promise<Invitation> {
  return unwrap<Invitation>(
    await authFetch(`/api/v1/admin/invitations/${id}/resend`, {
      method: 'POST',
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * POST /api/v1/admin/invitations/:id/revoke — revoke a pending invitation (Story 6.5,
 * AC3). System-Administrator only. Flips the invitation to `revoked` (its token no longer
 * activates; it drops from the pending roster) and audits. Returns 204; a 404 unknown id;
 * a 409 when not pending.
 */
export async function revokeInvitation(id: string): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/admin/invitations/${id}/revoke`, {
      method: 'POST',
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * POST /api/v1/admin/users — create a user directly (Story 6.6b, AC3/AC4).
 * System-Administrator only. Provisions the account immediately (active, bound to the
 * role + scope, MFA required for an admin) and emails the new user a set-password link —
 * the admin never sees or sets a password. Returns the created `UserItem`; a 400 on an
 * invalid role/scope combination; a 409 when the email already has an account.
 */
export async function createUser(input: CreateUserRequest): Promise<UserItem> {
  return unwrap<UserItem>(
    await authFetch('/api/v1/admin/users', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

/**
 * POST /api/v1/admin/users/:id/reset-password — trigger a set-password email for an
 * active user (Story 6.6b, AC1). System-Administrator only. The identity provider emails
 * a time-limited set-password link; no password is ever set by, returned to, or shown to
 * the admin. Returns 204; a 404 for an unknown or pending-invite id; a 409 for a
 * deactivated user (reactivate first).
 */
export async function resetUserPassword(id: string): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/admin/users/${id}/reset-password`, {
      method: 'POST',
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * PATCH /api/v1/admin/users/:id/profile — edit a user's profile (Story 6.6a, AC3).
 * System-Administrator only. Persists first/last name + contact phone to the identity
 * provider (preserving scope attributes) and audits — atomic. Returns 204; a 400 when
 * no field is provided; a 404 for an unknown or pending-invite id. Email, role, and
 * Company/Vendor scope are not editable here.
 */
export async function updateUserProfile(
  id: string,
  patch: UpdateUserProfileRequest,
): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/admin/users/${id}/profile`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(patch),
    }),
  );
}

/**
 * POST /api/v1/admin/users/:id/company — reassign a user to a different Company (Story
 * 6.6c, Capability E). System-Administrator only. Rewrites the user's Company binding in
 * both the roster and the identity provider and revokes their live sessions so the new
 * scope takes effect on next sign-in — all atomic. Returns the updated `UserItem`; a 400
 * when the companyId is not a UUID or names no existing Company; a 404 for an unknown or
 * pending-invite id; a 409 when the target is not a manager or is already in that Company.
 */
export async function reassignUserCompany(id: string, companyId: string): Promise<UserItem> {
  return unwrap<UserItem>(
    await authFetch(`/api/v1/admin/users/${id}/company`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ companyId }),
    }),
  );
}

/**
 * GET /api/v1/company/users — the Compliance Manager's own-Company People roster (Story
 * 6.5, AC5). Manager (or Admin) only; RLS scopes the read to the caller's own Company —
 * a Manager never receives another Company's users. Read-only (no management routes).
 */
export async function listCompanyPeople(): Promise<UserItem[]> {
  return unwrap<UserItem[]>(
    await authFetch('/api/v1/company/users', { headers: { accept: 'application/json' } }),
  );
}

/** POST /api/v1/invitations/activate — anonymous; returns the signed-in session. */
export async function activateInvitation(input: ActivateRequest): Promise<AuthSession> {
  const res = await fetch(`${API_BASE}/api/v1/invitations/activate`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(input),
  });
  const session = await unwrap<AuthSession>(res);
  accessToken = session.accessToken;
  return session;
}

/**
 * GET /api/v1/certificate-upload/:token — resolve the PUBLIC passwordless upload page
 * context (Story 6.9). No session/bearer — the token is the only credential. Returns
 * the requesting Company name, Framework, subject label, link expiry, and accepted
 * types. An invalid/expired/used token is a uniform 404 (surfaced as an Error).
 */
export async function getCertificateUploadContext(
  token: string,
): Promise<CertificateUploadContext> {
  const res = await fetch(`${API_BASE}/api/v1/certificate-upload/${encodeURIComponent(token)}`, {
    headers: { accept: 'application/json' },
  });
  return unwrap<CertificateUploadContext>(res);
}

/**
 * POST /api/v1/certificate-upload/:token — submit a document against a passwordless
 * upload token (Story 6.9). Multipart: the required evidence `file` plus optional
 * approving authority / expiry. No session/bearer. On success the document becomes a
 * `pending` declaration in the admin verification queue. A type/magic-byte mismatch or
 * infected file is a 422 (nothing stored, token still usable); an invalid/expired/used
 * token is a 404 — both surfaced as an Error.
 */
export async function uploadCertificateForToken(
  token: string,
  file: File,
  fields: { approvingAuthority?: string; expiresAt?: string } = {},
): Promise<CertificateUploadResult> {
  const form = new FormData();
  if (fields.approvingAuthority) form.append('approvingAuthority', fields.approvingAuthority);
  if (fields.expiresAt) form.append('expiresAt', fields.expiresAt);
  form.append('file', file);
  const res = await fetch(`${API_BASE}/api/v1/certificate-upload/${encodeURIComponent(token)}`, {
    method: 'POST',
    body: form,
  });
  return unwrap<CertificateUploadResult>(res);
}

/** GET /api/v1/me/settings — the caller's persisted UI preferences. */
export async function getSettings(): Promise<UserSettings> {
  return unwrap<UserSettings>(
    await authFetch('/api/v1/me/settings', { headers: { accept: 'application/json' } }),
  );
}

/** PATCH /api/v1/me/settings — persist theme/density for the caller. */
export async function patchSettings(patch: UserSettingsPatch): Promise<UserSettings> {
  return unwrap<UserSettings>(
    await authFetch('/api/v1/me/settings', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(patch),
    }),
  );
}

/** GET /api/v1/companies — admin: all Companies w/ counts; manager: own only. */
export async function listCompanies(): Promise<CompanyWithCounts[]> {
  return unwrap<CompanyWithCounts[]>(
    await authFetch('/api/v1/companies', { headers: { accept: 'application/json' } }),
  );
}

/** POST /api/v1/companies — System Administrator only. */
export async function createCompany(input: CreateCompanyRequest): Promise<Company> {
  return unwrap<Company>(
    await authFetch('/api/v1/companies', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

/**
 * GET /api/v1/frameworks — the Certification Catalog (Story 3.1). Platform-global
 * shared-read config; System Administrator only in the admin console.
 */
export async function listFrameworks(): Promise<Framework[]> {
  return unwrap<Framework[]>(
    await authFetch('/api/v1/frameworks', { headers: { accept: 'application/json' } }),
  );
}

/**
 * GET /api/v1/frameworks/applicable — the Certification Catalog filtered to the
 * caller's own Company (Story 3.2, FR-15). Compliance-Manager-scoped: returns
 * only the Frameworks that apply given the Company `maritime` flag, ordered by code.
 */
export async function listApplicableFrameworks(): Promise<Framework[]> {
  return unwrap<Framework[]>(
    await authFetch('/api/v1/frameworks/applicable', { headers: { accept: 'application/json' } }),
  );
}

/**
 * GET /api/v1/frameworks/:id/detail — the per-Framework detail for the caller's own
 * Company (Story 4.3). Manager-gated; `companyId` derived server-side. A device-scoped
 * Framework returns per-Vendor device counts; a company-scoped one returns per-Vendor
 * status/expiration/approving-authority. A maritime-only Framework for a non-maritime
 * Company (or an unknown id) is a 404 (hidden, not empty). Consumes the Epic 3 engine
 * (no new compute), reflecting current validity each session.
 */
export async function getFrameworkDetail(id: string): Promise<FrameworkDetail> {
  return unwrap<FrameworkDetail>(
    await authFetch(`/api/v1/frameworks/${id}/detail`, { headers: { accept: 'application/json' } }),
  );
}

/** POST /api/v1/frameworks — add a Framework to the catalog (System Administrator only). */
export async function createFramework(input: CreateFrameworkRequest): Promise<Framework> {
  return unwrap<Framework>(
    await authFetch('/api/v1/frameworks', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

/** PATCH /api/v1/frameworks/:id — partial edit of a Framework (System Administrator only). */
export async function updateFramework(
  id: string,
  patch: UpdateFrameworkRequest,
): Promise<Framework> {
  return unwrap<Framework>(
    await authFetch(`/api/v1/frameworks/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(patch),
    }),
  );
}

/**
 * Certificate + evidence client seam (Story 3.3) for Epic 4 to consume without
 * re-deriving the wire format. The rich attach/list/download cockpit UI is Epic 4;
 * these are the thin typed calls against the `/api/v1/certificates` surface.
 */

/**
 * POST /api/v1/certificates/submit-for-verification — submit a Certificate for platform
 * verification (Story 6.10, Compliance Manager). Multipart: the JSON fields plus the
 * required evidence `file` (the browser sets the multipart boundary, so it is NOT set
 * here — mirrors `declareCertificate`; `authFetch` still attaches the bearer token). The
 * submission lands in PRIVATE staging as "pending verification" — never the shared
 * backbone — until a System Administrator verifies it, whereupon it propagates read-time.
 */
export async function submitCertificateForVerification(
  input: SubmitCertificateForVerificationRequest,
  file: File,
): Promise<DeclaredCertificate> {
  const form = new FormData();
  form.append('frameworkId', input.frameworkId);
  form.append('subjectType', input.subjectType);
  form.append('vendorId', input.vendorId);
  if (input.deviceId) form.append('deviceId', input.deviceId);
  if (input.approvingAuthority) form.append('approvingAuthority', input.approvingAuthority);
  if (input.expiresAt) form.append('expiresAt', input.expiresAt);
  form.append('file', file);
  return unwrap<DeclaredCertificate>(
    await authFetch('/api/v1/certificates/submit-for-verification', {
      method: 'POST',
      headers: { accept: 'application/json' },
      body: form,
    }),
  );
}

/**
 * GET /api/v1/certificates/proposed?subjectType=&subjectId= — the manager's OWN company's
 * pending submissions for a subject (Story 6.10), rendered read-only as "pending
 * verification" in the cockpit. Company-scoped server-side (a manager never sees another
 * company's or a vendor's declarations).
 */
export async function listProposedForSubject(
  subjectType: CertificateSubjectType,
  subjectId: string,
): Promise<DeclaredCertificate[]> {
  const qs = new URLSearchParams({ subjectType, subjectId }).toString();
  return unwrap<DeclaredCertificate[]>(
    await authFetch(`/api/v1/certificates/proposed?${qs}`, { headers: { accept: 'application/json' } }),
  );
}

/**
 * GET /api/v1/certificates?subjectType=&subjectId= — the Certificates attached to a
 * Vendor or Device subject (shared-read).
 */
export async function listCertificatesForSubject(
  subjectType: CertificateSubjectType,
  subjectId: string,
): Promise<Certificate[]> {
  const qs = new URLSearchParams({ subjectType, subjectId }).toString();
  return unwrap<Certificate[]>(
    await authFetch(`/api/v1/certificates?${qs}`, { headers: { accept: 'application/json' } }),
  );
}

/** GET /api/v1/certificates/:id/evidence — list a Certificate's evidence rows. */
export async function listCertificateEvidence(
  certificateId: string,
): Promise<CertificateEvidence[]> {
  return unwrap<CertificateEvidence[]>(
    await authFetch(`/api/v1/certificates/${certificateId}/evidence`, {
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * GET /api/v1/certificates/:id/evidence/:eid/download — request a short-lived signed
 * download URL. Issued only after the API authorizes the caller's tenant/role and
 * (for a cross-tenant caller) the evidence is `clean`; otherwise 403.
 */
export async function getEvidenceDownloadUrl(
  certificateId: string,
  evidenceId: string,
): Promise<EvidenceDownloadResponse> {
  return unwrap<EvidenceDownloadResponse>(
    await authFetch(`/api/v1/certificates/${certificateId}/evidence/${evidenceId}/download`, {
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * Certificate-request client seam (Story 3.4, FR-12) for Epic 4 (dashboard request
 * affordance on a missing/expiring item) and Epic 6 (admin Requests inbox) to consume
 * without re-deriving the wire format. These are thin typed calls against the
 * `/api/v1/certificate-requests` surface; the rich UI is Epic 4 / Epic 6.
 */

/**
 * POST /api/v1/certificate-requests — record a per-Company request that a Vendor
 * supply a Certificate/information for a (Framework, subject). Compliance Manager
 * only; `companyId`/`status`/`onboardingRequired` are server-set. When the target
 * Vendor has no portal account, the response's `onboardingRequired` is `true` and a
 * durable System-Administrator onboarding flag is raised.
 */
export async function requestCertificate(
  input: CreateCertificateRequestBody,
): Promise<CertificateRequest> {
  return unwrap<CertificateRequest>(
    await authFetch('/api/v1/certificate-requests', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

/**
 * GET /api/v1/certificate-requests?subjectType=&subjectId= — requests for a Vendor or
 * Device subject. RLS scopes the read by role: a Compliance Manager sees only its own
 * Company's requests (another Company's are invisible); a System-Administrator sees all
 * requests across tenants (cross-tenant).
 */
export async function listCertificateRequests(
  subjectType: CertificateSubjectType,
  subjectId: string,
): Promise<CertificateRequest[]> {
  const qs = new URLSearchParams({ subjectType, subjectId }).toString();
  return unwrap<CertificateRequest[]>(
    await authFetch(`/api/v1/certificate-requests?${qs}`, {
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * GET /api/v1/certificate-requests/onboarding-flags — raised Vendor onboarding
 * flags across tenants (System-Administrator only; the Epic 6 Requests inbox reads
 * this to onboard Vendors with no portal account).
 */
export async function listVendorOnboardingFlags(): Promise<VendorOnboardingFlag[]> {
  return unwrap<VendorOnboardingFlag[]>(
    await authFetch('/api/v1/certificate-requests/onboarding-flags', {
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * Seed certification-database client seam (Story 3.5, FR-13, AR-5) for Epic 4 to consume
 * without re-deriving the wire format. These are thin typed calls against the
 * `/api/v1/seed-certificates` surface; the import/review UI is Epic 4 / Epic 6.
 */

/**
 * GET /api/v1/seed-certificates — browse the seed certification database. Each row is
 * annotated `matched` (its subject resolves to an existing Vendor/Device) and, when
 * already imported, `importedCertificateId`. Unmatched rows appear with `matched:false`
 * (surfaced for review). Readable by a Compliance Manager or System-Administrator.
 */
export async function listSeedCertificates(): Promise<SeedCertificateListing[]> {
  return unwrap<SeedCertificateListing[]>(
    await authFetch('/api/v1/seed-certificates', {
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * POST /api/v1/seed-certificates/:id/import — import a seed row into a real Certificate
 * (Compliance Manager only). Creates a `pending` Certificate carrying the seed row's
 * `source`/`approvingAuthority` and `provenance='imported-from-seed'` — never
 * trusted-by-policy. An unmatched subject is `422`; a re-import of the same row is `409`.
 */
export async function importSeedCertificate(id: string): Promise<Certificate> {
  return unwrap<Certificate>(
    await authFetch(`/api/v1/seed-certificates/${id}/import`, {
      method: 'POST',
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * GET /api/v1/sites — manager: own Company's Sites; admin: all.
 *
 * ACTIVE ONLY by default (DEV-4424 d): a deactivated (mothballed) Site is absent unless
 * `includeDeactivated` is passed. That default is what keeps every OTHER consumer of this
 * list correct with no change of its own — the report scope picker, the device-install
 * picker and the import wizard must not offer a retired vessel — so it is deliberately the
 * caller who opts IN, never out. Only the Sites screen's "Show deactivated" toggle does.
 *
 * The flag is OMITTED rather than sent as `false`, because the server reads
 * `includeDeactivated === 'true'` and anything else is the default: sending `false`
 * would be a second spelling of the same request.
 */
export async function getSites(includeDeactivated = false): Promise<Site[]> {
  const query = includeDeactivated ? '?includeDeactivated=true' : '';
  return unwrap<Site[]>(
    await authFetch(`/api/v1/sites${query}`, { headers: { accept: 'application/json' } }),
  );
}

/**
 * POST /api/v1/sites/:id/deactivate — mothball a Site (DEV-4424 d). Compliance Manager /
 * System Administrator, the same roles as `PATCH /sites/:id`.
 *
 * Reversible and lossless: it writes a timestamp and nothing else. The Site's vendors,
 * devices and certificates are all kept (see {@link reactivateSite}), but they stop
 * contributing to every compliance computation and the Site leaves the default list.
 *
 * Returns 204 — no envelope to unwrap. A 409 means the Site is already deactivated (no
 * second write, no duplicate activity entry); a 404 an unknown or cross-tenant id.
 */
export async function deactivateSite(id: string): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/sites/${id}/deactivate`, {
      method: 'POST',
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * POST /api/v1/sites/:id/reactivate — return a mothballed Site to service (DEV-4424 d).
 * Same roles as {@link deactivateSite}. Clears the timestamp, so every score, roster and
 * assignment returns to its pre-deactivation value with no re-entry of data.
 *
 * Returns 204; a 409 means the Site is already active, a 404 an unknown/cross-tenant id.
 */
export async function reactivateSite(id: string): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/sites/${id}/reactivate`, {
      method: 'POST',
      headers: { accept: 'application/json' },
    }),
  );
}

/** POST /api/v1/sites — Compliance Manager / System Administrator. */
export async function createSite(input: CreateSiteRequest): Promise<Site> {
  return unwrap<Site>(
    await authFetch('/api/v1/sites', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

/** PATCH /api/v1/sites/:id — partial update of an existing Site. */
export async function updateSite(id: string, patch: UpdateSiteRequest): Promise<Site> {
  return unwrap<Site>(
    await authFetch(`/api/v1/sites/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(patch),
    }),
  );
}

/**
 * GET /api/v1/vendors — the global Vendor Directory. Shared-read backbone: every
 * authenticated principal receives the identical tenant-less set of `vendor` objects,
 * in the same order (AD-19) — plus their own Company's proposals, which are RLS-scoped.
 *
 * The ENTRIES are not identical: `usedByCompany` (Story 7.3) is computed per caller and
 * is the one field permitted to differ between Companies. Anything reading it is reading
 * a fact about the CALLER's Company; it is `false` for a principal with no Company (an
 * admin or a vendor portal user), which is not the same as "not used".
 */
export async function getVendors(): Promise<VendorDirectoryEntry[]> {
  return unwrap<VendorDirectoryEntry[]>(
    await authFetch('/api/v1/vendors', { headers: { accept: 'application/json' } }),
  );
}

/**
 * POST /api/v1/vendors — propose a new Vendor into the shared backbone (Story
 * 2.5, Compliance Manager only). Enters an `unverified`, tenant-proposed state
 * tagged with the caller's Company (server-derived). Returns the new Vendor.
 */
export async function proposeVendor(input: ProposeVendorRequest): Promise<Vendor> {
  return unwrap<Vendor>(
    await authFetch('/api/v1/vendors', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

/** GET /api/v1/sites/:siteId/vendors — a Site's assigned Vendors (owning Manager / Admin). */
export async function getSiteVendors(siteId: string): Promise<SiteVendorAssignment[]> {
  return unwrap<SiteVendorAssignment[]>(
    await authFetch(`/api/v1/sites/${siteId}/vendors`, { headers: { accept: 'application/json' } }),
  );
}

/** POST /api/v1/sites/:siteId/vendors — assign a global Vendor to a Site (idempotent). */
export async function assignVendor(siteId: string, vendorId: string): Promise<SiteVendorAssignment> {
  return unwrap<SiteVendorAssignment>(
    await authFetch(`/api/v1/sites/${siteId}/vendors`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ vendorId }),
    }),
  );
}

/** DELETE /api/v1/sites/:siteId/vendors/:vendorId — remove only this Site's assignment (204). */
export async function removeSiteVendor(siteId: string, vendorId: string): Promise<void> {
  const res = await authFetch(`/api/v1/sites/${siteId}/vendors/${vendorId}`, {
    method: 'DELETE',
    headers: { accept: 'application/json' },
  });
  // 204 No Content — no envelope to unwrap; surface a non-OK status as an error.
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
  }
}

/**
 * GET /api/v1/devices — the minimal global Device catalog (install picker source).
 * Shared-read backbone: every authenticated principal receives the identical
 * tenant-less set (AD-19), mirroring the Vendor Directory.
 */
export async function getDevices(): Promise<DeviceCatalogEntry[]> {
  return unwrap<DeviceCatalogEntry[]>(
    await authFetch('/api/v1/devices', { headers: { accept: 'application/json' } }),
  );
}

/**
 * POST /api/v1/devices — propose a new Device into the shared backbone (Story
 * 2.5, Compliance Manager only). Returns the new catalog entry.
 *
 * The body names the manufacturer in **exactly one** of two ways (Story 8.9); both or
 * neither is a 400:
 *   - `vendorId` — an existing Vendor the caller can see (curated or their own
 *     proposal); an invisible or unknown id is a 404. Unchanged from Story 2.5.
 *   - `vendor` — an INLINE Vendor proposal, for when the manufacturer is absent from
 *     the directory too. The server mints it `unverified`, tagged with the caller's
 *     Company, in the SAME transaction as the Device: either both rows exist or
 *     neither does.
 *
 * Either way the 201 carries **both** rows — `DeviceCatalogEntry` is `{device, vendor}`
 * — so the caller can name what was created (including the vendor it just minted)
 * without a second read. No signature change was needed for 8.9: the widening is on
 * `ProposeDeviceRequest` itself.
 */
export async function proposeDevice(input: ProposeDeviceRequest): Promise<DeviceCatalogEntry> {
  return unwrap<DeviceCatalogEntry>(
    await authFetch('/api/v1/devices', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

/**
 * GET /api/v1/devices/duplicate-check — the propose-duplicate disclosure (Story 8.5,
 * AC3). Manager only. **Advisory: it creates nothing and refuses nothing** — its answer
 * warns the Manager that their visible catalog already holds this vendor + model (and
 * with which revisions) *before* they submit, while `proposeDevice` stays available
 * either way, because a second revision of one vendor + model is a legitimate catalog
 * state.
 *
 * Scoped to the caller's visible catalog (curated rows + their own Company's proposals),
 * so an empty `matches` means "nothing visible matched" and NEVER "this model is unique":
 * another tenant's proposal is invisible by construction (AR-6). Callers must treat a
 * rejected promise as "no warning to show", not as an error to surface — the check is a
 * courtesy over a shared-read catalog, not a uniqueness oracle.
 */
export async function checkDeviceDuplicate(
  vendorId: string,
  model: string,
): Promise<DeviceDuplicateCheckResponse> {
  const query = new URLSearchParams({ vendorId, model });
  return unwrap<DeviceDuplicateCheckResponse>(
    await authFetch(`/api/v1/devices/duplicate-check?${query.toString()}`, {
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * GET /api/v1/devices/directory — the fleet-wide Device Directory (Story 2.6,
 * FR-7). Compliance Manager only; tenant-scoped — each distinct Device in use
 * across the caller's Company Sites, with its manufacturer Vendor, the caller's
 * own installed-on Sites, and a neutral compliance seam.
 */
export async function getDeviceDirectory(): Promise<DeviceDirectoryEntry[]> {
  return unwrap<DeviceDirectoryEntry[]>(
    await authFetch('/api/v1/devices/directory', { headers: { accept: 'application/json' } }),
  );
}

/** GET /api/v1/sites/:siteId/devices — a Site's installed Devices (owning Manager / Admin). */
export async function getSiteDevices(siteId: string): Promise<DeviceInstall[]> {
  return unwrap<DeviceInstall[]>(
    await authFetch(`/api/v1/sites/${siteId}/devices`, { headers: { accept: 'application/json' } }),
  );
}

/** POST /api/v1/sites/:siteId/devices — install a global Device on a Site (idempotent). */
export async function installDevice(siteId: string, deviceId: string): Promise<DeviceInstall> {
  return unwrap<DeviceInstall>(
    await authFetch(`/api/v1/sites/${siteId}/devices`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ deviceId }),
    }),
  );
}

/**
 * DELETE /api/v1/sites/:siteId/devices/:deviceId — remove only this Site's install
 * (204). Non-destructive to the global Device; records a Removal History entry.
 */
export async function removeSiteDevice(siteId: string, deviceId: string): Promise<void> {
  const res = await authFetch(`/api/v1/sites/${siteId}/devices/${deviceId}`, {
    method: 'DELETE',
    headers: { accept: 'application/json' },
  });
  // 204 No Content — no envelope to unwrap; surface a non-OK status as an error.
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
  }
}

/** GET /api/v1/sites/:siteId/devices/removals — this Site's Removal History (newest-first). */
export async function getSiteRemovals(siteId: string): Promise<DeviceRemoval[]> {
  return unwrap<DeviceRemoval[]>(
    await authFetch(`/api/v1/sites/${siteId}/devices/removals`, {
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * POST /api/v1/imports — commit a fully-resolved bulk-import plan (Story 2.7,
 * FR-27, Compliance Manager only). Parsing + auto-matching happen client-side;
 * this posts one JSON plan. The server applies it atomically and re-validates
 * every reference — an invisible reference or invalid create rolls the whole
 * import back (404/400). Returns the per-import tally.
 */
export async function commitImport(req: ImportCommitRequest): Promise<ImportResult> {
  return unwrap<ImportResult>(
    await authFetch('/api/v1/imports', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(req),
    }),
  );
}

/**
 * GET /api/v1/dashboard — the Compliance Manager's fleet summary (Story 4.1).
 * Manager-gated; `companyId` derived server-side. Returns Sites/Vendors counts,
 * the Overall Score + band, per-applicable-Framework cards, the Needs-Attention
 * buckets (authoritative counts), and the per-Site status list — all consuming the
 * Epic 3 engine (no new compute), reflecting current validity each session.
 */
export async function getDashboard(): Promise<DashboardSummary> {
  return unwrap<DashboardSummary>(
    await authFetch('/api/v1/dashboard', { headers: { accept: 'application/json' } }),
  );
}

/**
 * GET /api/v1/activity — the 10 newest Recent Activity entries for the caller's own
 * Company (Story 4.1). Reads the forward-only `activity_feed` overlay (distinct from
 * the audit trail and the notification center); RLS company-scopes the read.
 */
export async function getActivity(): Promise<ActivityEntry[]> {
  return unwrap<ActivityEntry[]>(
    await authFetch('/api/v1/activity', { headers: { accept: 'application/json' } }),
  );
}

/**
 * GET /api/v1/dashboard/needs-attention — the FULL Needs-Attention surface (Story
 * 4.2, FR-19). Manager-gated; `companyId` derived server-side. Returns EVERY
 * expiring / expired-missing / unverified item (not the dashboard's 5-item preview),
 * pre-sorted worst-status-first then soonest expiry, with per-category counts
 * identical-by-construction to the dashboard tiles — all consuming the Epic 3 engine
 * (no new compute), reflecting current validity each session.
 */
export async function getNeedsAttention(): Promise<NeedsAttentionSurface> {
  return unwrap<NeedsAttentionSurface>(
    await authFetch('/api/v1/dashboard/needs-attention', { headers: { accept: 'application/json' } }),
  );
}

/**
 * GET /api/v1/dashboard/sites/:siteId — one Site's (vessel's) compliance (Story 7.2).
 * Manager-gated; `companyId` derived server-side, and a Site outside the caller's
 * tenant 404s indistinguishably from an unknown id. Returns the dashboard's OWN
 * Site-status row (`band` + `worstDriver`, byte-identical by construction) plus that
 * Site's pooled `score` and one card per applicable Framework — assembled by the same
 * engine pass as the fleet dashboard, narrowed by site membership. No client-side
 * arithmetic: every number here is server-computed.
 */
export async function getSiteCompliance(siteId: string): Promise<SiteComplianceDetail> {
  return unwrap<SiteComplianceDetail>(
    await authFetch(`/api/v1/dashboard/sites/${siteId}`, { headers: { accept: 'application/json' } }),
  );
}

/**
 * GET /api/v1/notifications — the Compliance Manager's LIVE at-risk list (Story
 * 4.5). Manager-gated; `companyId` derived server-side. Every item is derived from
 * the compliance engine on read (never the lagging ledger), with a stable key,
 * status pill input, and a `read` flag; `unreadCount` is the true unread total.
 */
export async function getNotifications(): Promise<NotificationList> {
  return unwrap<NotificationList>(
    await authFetch('/api/v1/notifications', { headers: { accept: 'application/json' } }),
  );
}

/**
 * POST /api/v1/notifications/read — mark one notification read by its stable key
 * (Story 4.5). Idempotent; returns 204. The `key` is the server-minted, transition-
 * qualified notification key (never a subject id).
 */
export async function markNotificationRead(key: string): Promise<void> {
  const res = await authFetch('/api/v1/notifications/read', {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ key }),
  });
  // 204 No Content — no envelope to unwrap; surface a non-OK status as an error.
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
  }
}

/**
 * GET /api/v1/vendor-portal/overview — the Vendor-role portal Overview (Story 5.1).
 * Vendor-gated; `vendorId` derived server-side. Returns the caller's OWN profile,
 * global verification status, aggregate-or-suppressed usage, alerts, and its
 * company-level & device-level Certificates — own data only. The payload is
 * structurally incapable of carrying any customer identity.
 */
export async function getVendorPortalOverview(): Promise<VendorPortalOverview> {
  return unwrap<VendorPortalOverview>(
    await authFetch('/api/v1/vendor-portal/overview', { headers: { accept: 'application/json' } }),
  );
}

/**
 * POST /api/v1/vendor-portal/certificates — declare + upload a Certificate for review
 * (Story 5.2). Multipart: the JSON fields plus the required evidence `file`. The
 * browser sets the multipart `content-type` boundary, so it is NOT set here (as in
 * `submitCertificateForVerification`); `authFetch` still attaches the bearer token. The declaration
 * lands in PRIVATE staging as "Declared · pending review" — never the shared backbone.
 */
export async function declareCertificate(
  input: DeclareCertificateRequest,
  file: File,
): Promise<DeclaredCertificate> {
  const form = new FormData();
  form.append('frameworkId', input.frameworkId);
  form.append('subjectType', input.subjectType);
  if (input.deviceId) form.append('deviceId', input.deviceId);
  if (input.approvingAuthority) form.append('approvingAuthority', input.approvingAuthority);
  if (input.expiresAt) form.append('expiresAt', input.expiresAt);
  form.append('file', file);
  return unwrap<DeclaredCertificate>(
    await authFetch('/api/v1/vendor-portal/certificates', {
      method: 'POST',
      headers: { accept: 'application/json' },
      body: form,
    }),
  );
}

/**
 * GET /api/v1/admin/verification-queue — the platform verification queue (Story 5.4).
 * System-Administrator only (RolesGuard denies any other principal 403). Returns the
 * pending "Declared · pending review" declarations with their framework, subject,
 * owning Vendor, approving authority, expiry, and evidence metadata. No customer
 * identity is present in any item (the proposal realm carries none).
 */
export async function listVerificationQueue(): Promise<VerificationQueueItem[]> {
  return unwrap<VerificationQueueItem[]>(
    await authFetch('/api/v1/admin/verification-queue', { headers: { accept: 'application/json' } }),
  );
}

/**
 * POST /api/v1/admin/verification-queue/:id/verify — verify (promote) a declaration
 * into the one shared backbone record under the same id, set the Vendor verified, and
 * audit — all atomic (Story 5.4). System-Administrator only. Returns 204. A 409 means
 * the declaration was already promoted; a 404 that it is no longer pending.
 */
export async function verifyDeclaration(id: string): Promise<void> {
  const res = await authFetch(`/api/v1/admin/verification-queue/${id}/verify`, {
    method: 'POST',
    headers: { accept: 'application/json' },
  });
  // 204 No Content — no envelope to unwrap; surface a non-OK status as an error.
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
  }
}

/**
 * POST /api/v1/admin/verification-queue/:id/reject — reject/hold a declaration with a
 * required non-empty reason (Story 5.4). System-Administrator only. The proposal stays
 * pending; the reason is audited and emailed to the Vendor. Returns 204; a blank
 * reason is a 400.
 */
export async function rejectDeclaration(id: string, reason: string): Promise<void> {
  const res = await authFetch(`/api/v1/admin/verification-queue/${id}/reject`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ reason }),
  });
  // 204 No Content — no envelope to unwrap; surface a non-OK status as an error.
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
  }
}

/**
 * GET /api/v1/admin/verification-queue/:itemId/evidence/:eid/content — stream a staged
 * declaration's evidence bytes for review (Story 5.4). System-Administrator only;
 * returns the raw object as a Blob (mirrors the Story 3.3 evidence download client).
 */
export async function downloadVerificationEvidence(itemId: string, eid: string): Promise<Blob> {
  const res = await authFetch(
    `/api/v1/admin/verification-queue/${itemId}/evidence/${eid}/content`,
    { headers: { accept: 'application/octet-stream' } },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
  }
  return res.blob();
}

/**
 * GET /api/v1/admin/certificates — the trusted backbone Certificates (Story 5.5).
 * System-Administrator only (RolesGuard denies any other principal 403). Returns the
 * verified (status='verified') backbone certs with their framework, subject, owning
 * Vendor, approving authority, expiry, and status. No customer identity is present in
 * any item (the backbone realm carries none).
 */
export async function listVerifiedCertificates(): Promise<VerifiedCertificateItem[]> {
  return unwrap<VerifiedCertificateItem[]>(
    await authFetch('/api/v1/admin/certificates', { headers: { accept: 'application/json' } }),
  );
}

/**
 * POST /api/v1/admin/certificates/:id/revoke — revoke a verified Certificate with a
 * required non-empty reason (Story 5.5). System-Administrator only. Moves the ONE
 * shared backbone record out of the trusted state in place; every Company using the
 * Vendor reflects the downgrade by shared-read. Returns 204; a blank reason is a 400,
 * an unknown id a 404, and a cert not in the verified state a 409.
 */
export async function revokeCertificate(id: string, reason: string): Promise<void> {
  const res = await authFetch(`/api/v1/admin/certificates/${id}/revoke`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ reason }),
  });
  // 204 No Content — no envelope to unwrap; surface a non-OK status as an error.
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
  }
}

/**
 * Surface a non-OK 204-action response as an Error carrying the envelope message
 * (shared by the Story 6.4 verify/merge action clients — no envelope to unwrap on 204).
 */
async function throwIfActionFailed(res: Response): Promise<void> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
  }
}

/**
 * GET /api/v1/admin/requests — the System-Administrator Requests inbox (Story 6.4,
 * FR-10/FR-12). System-Administrator only (RolesGuard denies any other principal 403).
 * Returns the proposed Vendors/Devices awaiting curation + the pending onboarding flags.
 */
export async function getRequestsInbox(): Promise<RequestsInbox> {
  return unwrap<RequestsInbox>(
    await authFetch('/api/v1/admin/requests', { headers: { accept: 'application/json' } }),
  );
}

/**
 * POST /api/v1/admin/requests/proposed-vendors/:id/verify — promote a proposed Vendor
 * into the shared backbone under the same id (Story 6.4). 204; 404 when not a proposal.
 */
export async function verifyProposedVendor(id: string): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/admin/requests/proposed-vendors/${id}/verify`, {
      method: 'POST',
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * POST /api/v1/admin/requests/proposed-vendors/:id/merge — dedup a proposed Vendor into
 * an existing backbone Vendor (Story 6.4). 204; 400 bad target; 409 fail-closed.
 */
export async function mergeProposedVendor(id: string, targetVendorId: string): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/admin/requests/proposed-vendors/${id}/merge`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ targetVendorId }),
    }),
  );
}

/**
 * POST /api/v1/admin/requests/proposed-devices/:id/verify — promote a proposed Device
 * into the shared backbone under the same id (Story 6.4). 204; 404 not a proposal; 409
 * when the owning Vendor is still proposed.
 */
export async function verifyProposedDevice(id: string): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/admin/requests/proposed-devices/${id}/verify`, {
      method: 'POST',
      headers: { accept: 'application/json' },
    }),
  );
}

/**
 * POST /api/v1/admin/requests/proposed-devices/:id/merge — dedup a proposed Device into
 * an existing backbone Device (Story 6.4). 204; 400 bad target; 409 fail-closed.
 *
 * `acknowledgeVersionLoss` (Story 8.5, AD-25) is required only when the merge would
 * DESTROY a distinguishing revision — the loser carries a non-empty one the winner does
 * not share. Without it the server 409s naming both revisions and deletes nothing; that
 * 409 is the guarantee, and the caller's confirm modal is only the affordance, so this
 * flag must never be sent by default.
 */
export async function mergeProposedDevice(
  id: string,
  targetDeviceId: string,
  acknowledgeVersionLoss = false,
): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/admin/requests/proposed-devices/${id}/merge`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({
        targetDeviceId,
        ...(acknowledgeVersionLoss ? { acknowledgeVersionLoss: true } : {}),
      }),
    }),
  );
}

/**
 * PATCH /api/v1/admin/requests/proposed-vendors/:id — curate a proposed Vendor BEFORE
 * promoting it (Story 8.12). 204; 400 on a blank/over-long value, a cleared required field or
 * an empty body; 404 when the id is not a pending proposal; 409 when it is a CURATED Vendor
 * (not editable at all — the route is propose → verify).
 *
 * Only the keys the caller supplies are sent, because a key's PRESENCE is what the server
 * reads as "consider this field": serialising the whole form would turn an untouched optional
 * field into an explicit `null` and clear it. `undefined` values are dropped by
 * `JSON.stringify`, so an explicit `null` — the CLEAR — still travels.
 */
export async function curateProposedVendor(
  id: string,
  body: CurateProposedVendorRequest,
): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/admin/requests/proposed-vendors/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
    }),
  );
}

/**
 * PATCH /api/v1/admin/requests/proposed-devices/:id — curate a proposed Device before
 * promoting it (Story 8.12). Same codes as {@link curateProposedVendor}; its 409 names
 * `PATCH /api/v1/admin/devices/:id` as the route for a CURATED device's revision.
 *
 * Unlike that endpoint this one DOES edit `model`: on a staging row nothing outside the
 * proposing Company has seen it, so correcting a typo before promotion changes no shared fact.
 * `vendorId` is editable on neither — re-pointing a reference is Merge's alone.
 */
export async function curateProposedDevice(
  id: string,
  body: CurateProposedDeviceRequest,
): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/admin/requests/proposed-devices/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
    }),
  );
}

/**
 * PATCH /api/v1/admin/devices/:id — record or clear a CURATED Device's hardware/firmware
 * revision (Story 8.5, AD-25 / AD-6). System-Administrator only; 204 on success, 400 on a
 * blank/over-long value, 404 for an unknown id, 409 when the id is a pending proposal
 * (Verify it first). `null` clears the revision; a blank string is refused server-side
 * rather than silently treated as a clear.
 *
 * This is the write half of the installed base's revision — `getDevices()` already
 * returns `version` and already admits an administrator, so the read half needed nothing.
 */
export async function updateCuratedDeviceVersion(
  id: string,
  version: string | null,
): Promise<void> {
  await throwIfActionFailed(
    await authFetch(`/api/v1/admin/devices/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ version }),
    }),
  );
}

/**
 * POST /api/v1/admin/requests/onboarding-flags/:id/resolve — onboard/invite the Vendor
 * behind a pending onboarding flag via the FR-3 magic-link flow (Story 6.4). Returns the
 * resolve result (a minted invitation + dev activation link when the Vendor had no
 * account; `invited:false` when it already had one). 400 bad email; 404 no pending flag.
 */
export async function resolveOnboarding(
  id: string,
  email: string,
): Promise<ResolveOnboardingResult> {
  return unwrap<ResolveOnboardingResult>(
    await authFetch(`/api/v1/admin/requests/onboarding-flags/${id}/resolve`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ email }),
    }),
  );
}

/**
 * POST /api/v1/reports — generate an on-demand PDF compliance report (Story 6.1,
 * FR-26/UX-DR17). Compliance-Manager only; `companyId` derived server-side. The
 * response is a raw `application/pdf` stream OUTSIDE the JSON envelope, so this does
 * a manual `res.ok` check and returns the body as a `Blob` (mirrors
 * `downloadVerificationEvidence`). A non-OK response carries the JSON error envelope,
 * surfaced as an `Error`. The scope (`frameworkIds`/`siteIds`/`asOf`) is identical for
 * both entry points; only the pre-set `frameworkIds` differ.
 */
export async function createReport(input: CreateReportRequest): Promise<Blob> {
  const res = await authFetch('/api/v1/reports', {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/pdf' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
  }
  return res.blob();
}

/**
 * Trigger a browser download of a `Blob` under `filename` via a transient anchor.
 * Same-origin blob URL, revoked after the click so it does not leak. Kept generic so
 * any future binary download can reuse it.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Release after the click has been dispatched.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Call GET /api/v1/health and unwrap the shared envelope. Public endpoint —
 * no auth needed; used by the shell to prove the SPA→API pipeline.
 */
export async function fetchHealth(signal?: AbortSignal): Promise<HealthStatus> {
  const res = await fetch(`${API_BASE}/api/v1/health`, {
    signal,
    headers: { accept: 'application/json' },
  });
  return unwrap<HealthStatus>(res);
}
